import { useEffect, useState, useContext } from "react";
import { default as axios } from "../axiosConfig";
import {
  Box,
  Typography,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Select,
  MenuItem,
  IconButton,
  Menu
} from "@mui/material";
import { Link, useNavigate } from "react-router-dom";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { Pagination } from "./Pagination";
import {
  API_GetJobs,
  API_UpdateJobStatus,
  API_CreateJobApplication,
  API_GetCandidateAppliedListingIds,
  API_GetManagerJobs,
} from "../Assets/ApiUrlContainer";
import UserContext from "../Contexts/UserContext";
import { Button } from "@mui/material";
import Snackbar from "@mui/material/Snackbar";
import MuiAlert from "@mui/material/Alert";

// Helper to deduplicate jobs by ListingId
function dedupeJobs(jobs) {
  const seen = new Set();
  return jobs.filter(job => {
    if (seen.has(job.ListingId)) return false;
    seen.add(job.ListingId);
    return true;
  });
}

const JobsPage = () => {
  const { user } = useContext(UserContext);
  const isEmployee = user?.RoleId === 2;
  const isHiringManager = user?.RoleId === 9;
  const [jobs, setJobs] = useState([]);
  const [appliedListingIds, setAppliedListingIds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("All");
  const [searchTerm, setSearchTerm] = useState("");
  const [triggerPageReset, setTriggerPageReset] = useState(false);
  const [typeFilter, setTypeFilter] = useState("All");
  const [locationFilter, setLocationFilter] = useState("All");
  const [dateFilter, setDateFilter] = useState("");
  const [anchorEl, setAnchorEl] = useState(null);
  const [currentJobId, setCurrentJobId] = useState(null);
  const [currentStatus, setCurrentStatus] = useState(null);

  // Pagination state
  const [listDisplayStartingIndex, setListDisplayStartingIndex] = useState(0);
  const [listDisplayLimit, setListDisplayLimit] = useState(10); // default page size

  const navigate = useNavigate();

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const handleSnackbarClose = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  useEffect(() => {
    // Fetch jobs from the backend
    const fetchJobs = async () => {
      try {
        if (user && user.CandidateId) {
          const candidateAppliedListingIds = await axios.get(`${API_GetCandidateAppliedListingIds}/${user.CandidateId}`);
          setAppliedListingIds(candidateAppliedListingIds.data.map((listing) => listing.JobListingId));
        }

        if (isHiringManager) {
          const res = await axios.get(`${API_GetManagerJobs}?email=${user.Email}`);
          setJobs(res.data.jobs || []);

        } else {
          const response = await axios.get(API_GetJobs);
          setJobs(response.data);
        }

      } catch (error) {
        console.error("Error fetching jobs:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchJobs();
  }, [user, isHiringManager]);


  // Always reset page to first when any filter/search changes
  useEffect(() => {
    setListDisplayStartingIndex(0);
  }, [statusFilter, typeFilter, locationFilter, dateFilter, searchTerm]);

  if (loading) {
    return <Typography>Loading...</Typography>;
  }

  const handleStatusChange = (event) => setStatusFilter(event.target.value);
  const handleTypeChange = (event) => setTypeFilter(event.target.value);
  const handleLocationChange = (event) => setLocationFilter(event.target.value);
  const handleDateChange = (event) => setDateFilter(event.target.value);

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
    setTriggerPageReset(true);
  };

  const handleMenuClick = (event, jobId, status) => {
    setAnchorEl(event.currentTarget);
    setCurrentJobId(jobId);
    setCurrentStatus(status);
  };

  const handleMenuClose = () => setAnchorEl(null);

  const handleStatusUpdate = async (newStatus) => {
    try {
      if (newStatus === "Closed") {
        const currentJob = jobs.find((job) => job.ListingId === currentJobId);
        if (currentJob.Status !== "Active") {
          alert('You can only close a job that has expired and is in "active" status.');
          return;
        }
      }

      const updatedJobs = jobs.map((job) =>
        job.ListingId === currentJobId ? { ...job, Status: newStatus } : job
      );

      setJobs(updatedJobs);

      await axios.put(API_UpdateJobStatus, {
        ListingId: currentJobId,
        Status: newStatus,
      });

      setAnchorEl(null);
    } catch (error) {
      console.error("Error updating job status:", error);
      setAnchorEl(null);
    }
  };

  const handleApplyToJob = async (jobId) => {
    try {
      await axios.post(API_CreateJobApplication, {
        CandidateId: user.CandidateId,
        JobId: jobId,
      });

      setSnackbar({
        open: true,
        message: "Application submitted!",
        severity: "success",
      });

      setAppliedListingIds([...appliedListingIds, jobId]);
    } catch (error) {
      console.error("Error applying to job:", error);
      setSnackbar({
        open: true,
        message: "Failed to apply. Please try again.",
        severity: "error",
      });
    }
  };

  // 1. Filter jobs
  let filteredJobs = jobs.filter((job) => {
    const matchesSearch = (job.Title || "")
      .toLowerCase()
      .includes(searchTerm.toLowerCase());

    const matchesStatus =
      statusFilter === "All" || job.Status === statusFilter;

    const matchesType =
      typeFilter === "All" || (job.Type && job.Type === typeFilter);

    const matchesLocation =
      locationFilter === "All" ||
      (job.Location && job.Location === locationFilter);

    return matchesSearch && matchesStatus && matchesType && matchesLocation;
  });


  // 2. Dedupe jobs by ListingId (if any duplicates ever exist)
  filteredJobs = dedupeJobs(filteredJobs);

  // 3. Sort jobs
  if (dateFilter === "desc") {
    filteredJobs = [...filteredJobs].sort(
      (a, b) => new Date(b.PostDate) - new Date(a.PostDate)
    );
  } else if (dateFilter === "asc") {
    filteredJobs = [...filteredJobs].sort(
      (a, b) => new Date(a.PostDate) - new Date(b.PostDate)
    );
  }

  // 4. Pagination
  const paginatedJobs = filteredJobs.slice(listDisplayStartingIndex, listDisplayLimit);

  const handlePageChange = (newPositionIndex) => setListDisplayStartingIndex(newPositionIndex);
  const handlePageListLimitChange = (newListDisplayLimit) => setListDisplayLimit(newListDisplayLimit);

  return (
    <div className="tgs-content">
      <Box sx={{ padding: 2 }} className="jobsListContainer">
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          sx={{ marginBottom: 2 }}
        >
          <Typography variant="h4">Jobs</Typography>
          {/* <IconButton>
            <SyncIcon />
          </IconButton> */}
        </Box>

        <Box
          display="flex"
          alignItems="center"
          gap={2}
          sx={{
            marginBottom: 2,
            flexWrap: "wrap",
            rowGap: 2,
          }}
        >
          <TextField
            label="Search"
            variant="outlined"
            value={searchTerm}
            onChange={handleSearchChange}
            sx={{
              width: 300,
              backgroundColor: "#f0f0f0",
            }}
          />
          <Select
            value={statusFilter}
            onChange={handleStatusChange}
            variant="outlined"
            sx={{ width: 140, backgroundColor: "#f5f5f5" }}
          >
            <MenuItem value="All">Status</MenuItem>
            <MenuItem value="Open">Open</MenuItem>
            <MenuItem value="Closed">Closed</MenuItem>
            <MenuItem value="Active">Active</MenuItem>
            <MenuItem value="Paused">Paused</MenuItem>
          </Select>
          <Select
            value={typeFilter}
            onChange={handleTypeChange}
            variant="outlined"
            sx={{ width: 140, backgroundColor: "#f5f5f5" }}
          >
            <MenuItem value="All">All Types</MenuItem>
            <MenuItem value="Full-time">Full-time</MenuItem>
            <MenuItem value="Part-time">Part-time</MenuItem>
            <MenuItem value="Contract">Contractor</MenuItem>
            <MenuItem value="Gig">Gig</MenuItem>
          </Select>
          <Select
            value={locationFilter}
            onChange={handleLocationChange}
            variant="outlined"
            sx={{ width: 200, backgroundColor: "#f5f5f5" }}
          >
            <MenuItem value="All">All Locations</MenuItem>
            {[...new Set(jobs.map((job) => job.Location))]
              .filter((location) => location)
              .map((location) => (
                <MenuItem key={location} value={location}>
                  {location}
                </MenuItem>
              ))}
          </Select>
          <Select
            value={dateFilter}
            onChange={handleDateChange}
            variant="outlined"
            sx={{ width: 180, backgroundColor: "#f5f5f5" }}
            displayEmpty
          >
            <MenuItem value="">Sort by Date</MenuItem>
            <MenuItem value="desc">Newest First</MenuItem>
            <MenuItem value="asc">Oldest First</MenuItem>
          </Select>
        </Box>

        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Role</TableCell>
                <TableCell>Applications</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Location</TableCell>
                <TableCell>Post Date</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedJobs.map((job) => (
                <TableRow
                  key={job.Id}
                  hover
                  style={{ cursor: "pointer" }}
                >
                  {/* Role */}
                  <TableCell>
                    <Link
                      to={`/jobs/${job.ListingId}`}
                      style={{ textDecoration: "none", color: "inherit" }}
                      className="jobName"
                    >
                      {job.Title || "N/A"}
                    </Link>
                  </TableCell>
                  {/* Applications */}
                  <TableCell>
                    {job.Applications !== undefined ? job.Applications : 0}
                  </TableCell>
                  {/* Status */}
                  <TableCell>{job.Status || "N/A"}</TableCell>
                  {/* Type */}
                  <TableCell>{job.Type || "N/A"}</TableCell>
                  {/* Location */}
                  <TableCell>{job.Location || "N/A"}</TableCell>
                  {/* Post Date */}
                  <TableCell>
                    {job.PostDate
                      ? new Date(job.PostDate).toLocaleDateString("en-US", {
                        month: "short",
                        day: "numeric",
                        year: "numeric",
                      })
                      : "N/A"}
                  </TableCell>
                  {/* Actions */}
                  <TableCell>
                    {isEmployee && !!user.CandidateId ? (
                      <Button
                        variant="contained"
                        color="primary"
                        size="small"
                        disabled={appliedListingIds.includes(job.ListingId)}
                        onClick={() => handleApplyToJob(job.ListingId)}
                      >
                        {appliedListingIds.includes(job.ListingId)
                          ? "Applied"
                          : "Apply"}
                      </Button>
                    ) : (
                      <IconButton
                        onClick={(e) =>
                          handleMenuClick(e, job.ListingId, job.Status)
                        }
                      >
                        <MoreVertIcon />
                      </IconButton>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
        >
          <MenuItem onClick={() => handleStatusUpdate("Open")}>Open</MenuItem>
          <MenuItem onClick={() => handleStatusUpdate("Closed")}>
            Closed
          </MenuItem>
          <MenuItem onClick={() => handleStatusUpdate("Active")}>
            Active
          </MenuItem>
          <MenuItem onClick={() => handleStatusUpdate("Paused")}>
            Paused
          </MenuItem>
        </Menu>

        <Pagination
          totalNumItems={filteredJobs.length}
          onPageChangeNewPosition={handlePageChange}
          onRowsChangeNewDisplayLimit={handlePageListLimitChange}
          triggerPageReset={triggerPageReset}
          onTriggerReset={setTriggerPageReset}
        />
      </Box>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <MuiAlert
          elevation={6}
          variant="filled"
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
        >
          {snackbar.message}
        </MuiAlert>
      </Snackbar>
    </div>
  );
};

export default JobsPage;